import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbExternalLink, TbFolderSymlink, TbLink, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';

import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useInvestmentStore } from '@/stores';
import { Investment } from '@/types';

import { getWorkspaceResourceRemovalActionLabel, WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface InvestmentsActionsDropdownProps {
    investment: Investment;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
}

const InvestmentsActionsDropdown: FC<InvestmentsActionsDropdownProps> = ({
    investment,
    children,
    onOpenChange,
    open,
}): JSX.Element => {
    const [renameVisible, setRenameVisible] = useState(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);

    const {
        investments,
        toggleInvestmentFavorite,
        duplicateInvestment,
        toggleInvestmentLock,
        toggleInvestmentArchive,
    } = useInvestmentStore();
    const access = useWorkspaceResourceAccess('investments', investment?.id);
    const createAccess = useWorkspaceResourceCreateAccess('investments', investments?.length ?? 0);

    const handleToggleFavorite = async () => {
        toggleInvestmentFavorite(investment?.id, !investment?.isFavorite);
    };

    const handleToggleLock = async () => {
        toggleInvestmentLock(investment?.id, !investment?.isLocked);
    };

    const handleToggleArchive = async () => {
        toggleInvestmentArchive(investment?.id, !investment?.isArchived);
    };

    const handleDuplicateInvestment = async () => {
        if (createAccess.disabled) {
            return;
        }

        duplicateInvestment(investment?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'favorited',
            icon: investment?.isFavorite ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
            label: investment?.isFavorite ? 'Remove from Favorites' : 'Add to Favorites',
            onClick: handleToggleFavorite,
            permission: 'update',
        },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={investment?.isLocked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', investment?.isLocked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={investment?.isArchived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', investment?.isArchived),
            onClick: handleToggleArchive,
            permission: 'manage',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/investments/${investment?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: investment?.editedName,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: investment?.editedName,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        {
            key: 'openInNewWindow',
            icon: <TbExternalLink size={'1rem'} />,
            label: 'Open in New Window',
            onClick: () => {
                window.open(`/investments/${investment?.id}`, '_blank');
            },
            permission: 'read',
        },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            disabled: createAccess.disabled,
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateInvestment,
            permission: 'read',
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/investments/${investment?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Investment ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.editedName,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        {
            key: 'moveToFolder',
            icon: <TbFolderSymlink size='1rem' />,
            label: 'Move to Folder',
            onClick: () => {
                setMoveToFolderVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: getWorkspaceResourceRemovalActionLabel('investments'),
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{
                    resourceId: investment.id,
                    resourceType: 'investments',
                    title: investment.editedName || 'Untitled Investment',
                }}
                items={items}
                onOpenChange={onOpenChange}
                open={open}
                resourceLabel='Investment'
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            <RenameResourceModal
                open={renameVisible}
                setOpen={setRenameVisible}
                resource={investment}
                singularResource='investment'
            />
            <MoveResourceToFolderModal
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
                resource={investment}
                singularResource='investment'
            />
            <DeleteResourceModal
                open={deleteVisible}
                setOpen={setDeleteVisible}
                resource={investment}
                singularResource='investment'
            />
        </>
    );
};

export default InvestmentsActionsDropdown;
